import { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { CultureCode, CurrencyCode, LanguageCode, constructLanguageCodeIso15897 } from '@shared/hooks/useGeoLocale';

export interface SupportedCurrency
  extends Readonly<{
    code: CurrencyCode;
    locale: string;
    culture: CultureCode;
    language: LanguageCode;
  }> {}

export const SUPPORTED_CURRENCIES: readonly SupportedCurrency[] = [
  {
    code: CurrencyCode.AUD,
    culture: CultureCode.AU,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.AU),
  },
  {
    code: CurrencyCode.CAD,
    culture: CultureCode.CA,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.CA),
  },
  {
    code: CurrencyCode.EUR,
    culture: CultureCode.DE,
    language: LanguageCode.de,
    locale: constructLanguageCodeIso15897(LanguageCode.de, CultureCode.DE),
  },
  {
    code: CurrencyCode.GBP,
    culture: CultureCode.GB,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.GB),
  },
  {
    code: CurrencyCode.JPY,
    culture: CultureCode.JP,
    language: LanguageCode.ja,
    locale: constructLanguageCodeIso15897(LanguageCode.ja, CultureCode.JP),
  },
  {
    code: CurrencyCode.USD,
    culture: CultureCode.US,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.US),
  },
];

export const useSupportedCurrencyOptions = (currencies: ReadonlyArray<SupportedCurrency> = SUPPORTED_CURRENCIES): ReadonlyArray<SupportedCurrency & { name: string }> => {
  const { tq } = useTranslation('currencies');

  return useMemo(() => {
    const translateName = (code: CurrencyCode) => {
      switch (code) {
        case CurrencyCode.USD:
          return tq`United States Dollar`;
        case CurrencyCode.EUR:
          return tq`Euro`;
        case CurrencyCode.GBP:
          return tq`Pound Sterling`;
        case CurrencyCode.CAD:
          return tq`Canadian Dollar`;
        case CurrencyCode.AUD:
          return tq`Australian Dollar`;
        case CurrencyCode.JPY:
          return tq`Yen`;
        case CurrencyCode.NZD:
          return tq`New Zealand Dollar`;
        default:
          return code;
      }
    };

    return currencies.map(o => ({
      ...o,
      name: translateName(o.code),
    }));
  }, [currencies, tq]);
};

export const SUGGESTED_CURRENCIES = ['EUR', 'USD', 'AUD'];

const CURRENCY_MAP = new Map(SUPPORTED_CURRENCIES.map(currency => [currency.code, currency]));

export const getCurrencyByCode = (code: string): SupportedCurrency | undefined => {
  return CURRENCY_MAP.get(code as CurrencyCode);
};

export const getLocaleFromCurrency = (currencyCode: string) => {
  const currency = getCurrencyByCode(currencyCode);

  if (!currency) {
    return {
      culture: CultureCode.US,
      language: LanguageCode.en,
      currency: CurrencyCode.USD,
      locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.US),
    };
  }

  return {
    locale: currency.locale,
    currency: currency.code,
    culture: currency.culture,
    language: currency.language,
  };
};
